import moment from 'moment';
import React from 'react';

import {
  IconBarrierBlock,
  IconEye,
  IconEyeClosed,
  IconRotate,
  IconCircleCheck,
  IconClockPlus,
  IconCurrencyDollar,
  IconExclamationCircle,
} from '@tabler/icons-react';

import { Badge } from 'src/design/atoms/Badge';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Tabs } from 'src/design/molecules/Tabs';
import { PostDto, PostDocumentDto } from 'src/api/FiretrolApiClient';
import { useResolveAttachmentUrl } from 'src/api/queries/attachments/useAttachmentMutations';
import { useDownloadDocument } from 'src/api/queries/documents/useDocumentDownload';
import { useResolvePost } from 'src/api/queries/posts/usePostMutations';
import { useForceReadPostNotification } from 'src/api/queries/userNotifications/useUserNotificationMutations';
import { useIntersection } from 'src/hooks/useIntersection';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { formatDate, formatDateTime, humanFileSize } from 'src/utils';
import { useAuth } from 'src/hooks/useAuth';
import { fileIcon } from 'src/app/documents/fileIcons';
import { MentionInput } from 'src/routes/Jobs/ActivityFeed/components/PostCard/components/MentionInput';
import { PostLinks } from 'src/routes/Jobs/ActivityFeed/components/PostCard/components/PostLinks';
import { Image } from './components/Image';
import { PostComments } from './components/PostComments';
import { PostMenu } from './components/PostMenu';

interface PostItemProps {
  item: PostDto;
  openEditDialog: (item: PostDto) => void;
  includeJobTitle?: boolean;
  jobId?: number;
}

const acceptedImageTypes = ['gif', 'jpeg', 'png', 'jpg'];

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <Paper p="md" withBorder>
    <Text ta="center" c="dimmed">
      {message}
    </Text>
  </Paper>
);

export const PostItem: React.FC<PostItemProps> = ({
  item,
  openEditDialog,
  includeJobTitle = false,
  jobId,
}) => {
  const [tab, setTab] = React.useState<string | null>('comments');
  const [read, setRead] = React.useState(false);
  const [shouldPoll, setShouldPoll] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const isVisible = useIntersection(ref, '50px');
  const { user } = useAuth();

  // A post opened from the global feed can carry no job, and every post route
  // is job-scoped, so nothing job-bound may be requested without a real id.
  const effectiveJobId = item.jobId ?? jobId;
  const hasJob = effectiveJobId != null && effectiveJobId > 0;
  const forceRead = useForceReadPostNotification();
  const resolvePost = useResolvePost(effectiveJobId);
  const downloadAttachment = useResolveAttachmentUrl();
  const downloadDocument = useDownloadDocument();

  const { mutate: markRead } = forceRead;

  React.useEffect(() => {
    setShouldPoll(isVisible);
    if (isVisible && !read) {
      markRead(item.id);
      setRead(true);
    }
  }, [isVisible, read, item.id, markRead]);

  const downloadFile = (id: string) => downloadAttachment.mutate(id);

  const downloadDoc = (doc: PostDocumentDto) => {
    const documentJobId = doc.jobId ?? item.jobId ?? jobId;
    if (!documentJobId) return;
    downloadDocument.mutate({ jobId: documentJobId, documentId: doc.id });
  };

  const images = React.useMemo(() => {
    return item.files.filter((f) => acceptedImageTypes.indexOf(f.type) >= 0);
  }, [item.files]);

  const nonImages = React.useMemo(() => {
    return item.files.filter((f) => acceptedImageTypes.indexOf(f.type) < 0);
  }, [item.files]);

  const renderImages = () => {
    if (images.length === 0) {
      return <EmptyState message="No Images" />;
    }
    return images.map((att) => (
      <Image
        key={att.id}
        id={att.id}
        disabled
        title={att.name}
        onRemoveClick={console.log}
        onImageClick={() => downloadFile(att.id)}
      />
    ));
  };

  const renderDocuments = () => {
    if (item.documents.length === 0) {
      return <EmptyState message="No Documents" />;
    }
    return item.documents.map((file) => (
      <Paper key={file.id} withBorder p="xs" mb="xs">
        <Group wrap="nowrap">
          <Box>{fileIcon(file.type)}</Box>
          <Box style={{ flex: 2 }}>
            <BasicButton onClick={() => downloadDoc(file)} variant="subtle">
              {file.name}.{file.type}
            </BasicButton>
          </Box>
          <Box style={{ flex: 1 }}>
            <Text ta="right" c="dimmed">
              {humanFileSize(file.size ?? 0)}
            </Text>
          </Box>
        </Group>
      </Paper>
    ));
  };

  const renderFiles = () => {
    if (nonImages.length === 0) {
      return <EmptyState message="No Files" />;
    }
    return nonImages.map((file) => (
      <Paper key={file.id} withBorder p="xs" mb="xs">
        <Group wrap="nowrap">
          <Box>{fileIcon(file.type)}</Box>
          <Box style={{ flex: 2 }}>
            <BasicButton onClick={() => downloadFile(file.id)} variant="subtle">
              {file.name}
            </BasicButton>
          </Box>
          <Box style={{ flex: 1 }}>
            <Text ta="right" c="dimmed">
              {humanFileSize(file.size)}
            </Text>
          </Box>
        </Group>
      </Paper>
    ));
  };

  const links = React.useMemo(() => {
    return [...item.meetings, ...item.dailys, ...item.rfis, ...item.changes];
  }, [item]);

  const handleEditClick = () => {
    openEditDialog(item);
  };

  const resolved = item.resolved;

  const toggleResolved = () => {
    if (!hasJob) return;
    resolvePost.mutate({ postId: item.id, resolved: !resolved });
  };

  const createdDate = moment(item.createdAt);
  const timeago = moment().diff(createdDate, 'days') <= 1;

  return (
    <Paper ref={ref}>
      <Stack>
        <Group justify="space-between" align="flex-start">
          <Stack gap="xs">
            <Title order={4} pb={0}>
              {item.subject ?? 'No Subject'}
            </Title>
            <Group gap="sm">
              <Text size="xs">By {item.createdBy?.fullName}</Text>
              {includeJobTitle && (
                <Text size="xs">
                  | {item.job.jobNumber} - {item.job.name}
                </Text>
              )}
            </Group>

            <Group gap="xs">
              {item.visibility === 'public' ? (
                <Badge variant="light" leftSection={<IconEye size={14} />}>
                  Public
                </Badge>
              ) : (
                <Badge
                  variant="light"
                  leftSection={<IconEyeClosed size={14} />}
                >
                  Private
                </Badge>
              )}
              {item.ctc && (
                <Badge
                  variant="light"
                  leftSection={<IconCurrencyDollar size={14} />}
                >
                  CTC
                </Badge>
              )}
              {item.constraint && (
                <Badge
                  variant="light"
                  leftSection={<IconBarrierBlock size={14} />}
                >
                  Constraint
                </Badge>
              )}
              {item.critical && (
                <Badge
                  variant="light"
                  leftSection={<IconExclamationCircle size={14} />}
                >
                  Critical
                </Badge>
              )}
              {item.cost && (
                <Badge
                  variant="light"
                  leftSection={<IconClockPlus size={14} />}
                >
                  Cost & Schedule
                </Badge>
              )}
              {item.assignedTo && (
                <Badge>Assigned: {item.assignedTo.fullName}</Badge>
              )}
              {item.date && <Badge>Due: {formatDate(item.date)}</Badge>}
            </Group>
          </Stack>

          <Group gap="xs">
            <Text size="sm" c="dimmed">
              {timeago
                ? createdDate.fromNow()
                : formatDateTime(item.createdAt, 'MM/DD/YYYY [at] h:mm a')}
            </Text>
            {resolved ? (
              <BasicActionIcon
                color="green"
                onClick={toggleResolved}
                disabled={!hasJob}
                label="Mark as Unresolved"
              >
                <IconCircleCheck size={16} />
              </BasicActionIcon>
            ) : (
              <BasicActionIcon
                label="Mark as Resolved"
                onClick={toggleResolved}
                disabled={!hasJob}
              >
                <IconRotate size={16} />
              </BasicActionIcon>
            )}

            {user?.id === item.createdById && (
              <PostMenu
                postId={item.id}
                jobId={item.jobId}
                onEditClicked={handleEditClick}
              />
            )}
          </Group>
        </Group>
        <MentionInput
          peopleOptions={[]}
          value={item.content}
          disabled
          onChange={console.log}
          id={item.id}
          label=""
        />
        {images.length > 0 && <Box mt="md">{renderImages()}</Box>}
        <Tabs value={tab} onChange={setTab}>
          <Tabs.List>
            <Tabs.Tab
              value="comments"
              rightSection={
                <Badge size="sm" variant="light">
                  {item.comments.length}
                </Badge>
              }
            >
              Comments
            </Tabs.Tab>
            <Tabs.Tab
              value="links"
              rightSection={
                <Badge size="sm" variant="light">
                  {links.length}
                </Badge>
              }
            >
              Links
            </Tabs.Tab>
            <Tabs.Tab
              value="files"
              rightSection={
                <Badge size="sm" variant="light">
                  {nonImages.length}
                </Badge>
              }
            >
              Files
            </Tabs.Tab>
            <Tabs.Tab
              value="documents"
              rightSection={
                <Badge size="sm" variant="light">
                  {item.documents.length}
                </Badge>
              }
            >
              Documents
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="comments">
            <PostComments
              comments={item.comments}
              postId={item.id}
              shouldPoll={shouldPoll}
              jobId={effectiveJobId}
            />
          </Tabs.Panel>

          <Tabs.Panel value="links">
            <PostLinks links={links} onLinkRemoved={console.log} disabled />
          </Tabs.Panel>

          <Tabs.Panel value="files">{renderFiles()}</Tabs.Panel>

          <Tabs.Panel value="documents">{renderDocuments()}</Tabs.Panel>
        </Tabs>
      </Stack>
    </Paper>
  );
};
